import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useSessionStore } from '../store/useSessionStore';

interface SplashScreenProps {
  /**
   * Whether the partner theme (CSS vars + font CDN) has finished applying
   * and the partner-logo request has settled. Until this is `true` we paint
   * a NEUTRAL spinner on a white surface — no brand colour, no brand name,
   * nothing that depends on `--font-sans` — so the first paint never flashes
   * default tokens before the partner palette/logo land.
   */
  brandReady: boolean;
  /**
   * Blob URL for the partner logo (`GET /widget/links/:linkId/logo`), or
   * `null` when the partner has no logo uploaded. When present it is shown
   * centered on the white splash (same pattern as fund-request).
   */
  logoSrc?: string | null;
}

/**
 * Full-screen brand splash shown while the wizard waits for the magic-link
 * claim, the partner theme, and the partner logo to finish loading.
 *
 * Two phases, cross-faded by an internal `AnimatePresence`:
 *   1. `brandReady === false` → a neutral spinner on a white surface.
 *   2. `brandReady === true` → white surface with the partner logo centered.
 *      Falls back to the partner's header name when no logo is available.
 *
 * The root is animated so the parent `AnimatePresence` (mode="wait") can play
 * the exit when the splash gives way to the app.
 */
export const SplashScreen = ({ brandReady, logoSrc = null }: SplashScreenProps) => {
  const { t } = useTranslation('smart');
  const partner = useSessionStore((s) => s.session?.partner);
  const brandName =
    partner?.headerName ?? partner?.name ?? t('landing.brand', 'Lola Send');

  return (
    <AnimatePresence mode="wait">
      {brandReady ? (
        <motion.main
          key="brand"
          className="fixed inset-0 z-50 grid place-items-center bg-white px-6"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.12 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {logoSrc ? (
            <motion.img
              src={logoSrc}
              alt={partner?.name ?? brandName}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.1,
              }}
              className="block h-auto w-auto max-h-[min(160px,35vh)] max-w-[min(240px,70vw)] object-contain"
            />
          ) : (
            <motion.h1
              aria-live="polite"
              initial={{ opacity: 0, y: 14, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.1,
              }}
              className="text-center text-4xl font-extrabold uppercase tracking-[0.25em] text-neutral-900 sm:text-5xl"
            >
              {brandName}
            </motion.h1>
          )}
        </motion.main>
      ) : (
        <motion.main
          key="neutral"
          className="fixed inset-0 z-50 grid place-items-center bg-white px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <motion.span
            aria-hidden
            className="inline-block h-10 w-10 rounded-full border-4 border-neutral-200 border-t-neutral-700"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          />
        </motion.main>
      )}
    </AnimatePresence>
  );
};
